import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Space, 
  Typography, 
  DatePicker, 
  Input, 
  Select, 
  Statistic, 
  Row, 
  Col,
  Tag,
  message,
  Modal,
  Descriptions,
  Spin
} from 'antd';
import { 
  ReloadOutlined, 
  SearchOutlined, 
  DollarOutlined, 
  UserOutlined,
  CalendarOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import apiClient from '../utils/apiClient';
import { useSelector } from 'react-redux';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const AdminPaymentHistory = () => {
  const { user } = useSelector(state => state.auth);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [filters, setFilters] = useState({
    user_id: null,
    start_date: null,
    end_date: null
  });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);

  useEffect(() => {
    if (user && user.is_admin) {
      loadPaymentHistory();
      loadPaymentStats();
    }
  }, [user, pagination.current, pagination.pageSize, filters]);

  const loadPaymentHistory = async () => {
    setLoading(true);
    try {
      const params = {
        skip: (pagination.current - 1) * pagination.pageSize,
        limit: pagination.pageSize
      };

      if (filters.user_id) {
        params.user_id = filters.user_id;
      }
      if (filters.start_date) {
        params.start_date = filters.start_date;
      }
      if (filters.end_date) {
        params.end_date = filters.end_date;
      }

      const response = await apiClient.get('/api/payment-scanner/admin/payment-history', { params });
      
      if (response.data.success) {
        setTransactions(response.data.data);
        setPagination(prev => ({
          ...prev,
          total: response.data.total
        }));
      }
    } catch (error) {
      console.error('Error loading payment history:', error);
      message.error('Unable to load payment history');
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentStats = async () => {
    setStatsLoading(true);
    try {
      const response = await apiClient.get('/api/payment-scanner/admin/payment-stats?days=30');
      
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error loading payment stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
    loadPaymentHistory();
  };

  const handleReset = () => {
    setFilters({
      user_id: null,
      start_date: null,
      end_date: null
    });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const showTransactionDetail = (transaction) => {
    setSelectedTransaction(transaction);
    setDetailModalVisible(true);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(price);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('vi-VN');
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'User',
      key: 'user',
      render: (_, record) => (
        <div>
          <div><Text strong>{record.user_name}</Text></div>
          <div><Text type="secondary" style={{ fontSize: '12px' }}>{record.user_email}</Text></div>
        </div>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => (
        <Text strong style={{ color: '#52c41a' }}>
          {formatPrice(amount)}
        </Text>
      ),
    },
    {
      title: 'Plan',
      key: 'plan',
      render: (_, record) => (
        <div>
          <div><Text strong>{record.plan_name}</Text></div>
          <div><Text type="secondary" style={{ fontSize: '12px' }}>+{record.plan_chars.toLocaleString()} chars</Text></div>
        </div>
      ),
    },
    {
      title: 'Reference',
      dataIndex: 'content',
      key: 'content',
      ellipsis: true,
      render: (content) => (
        <Text code style={{ fontSize: '12px' }}>
          {content}
        </Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'processed' ? 'green' : 'orange'}>
          {status === 'processed' ? 'Processed' : 'Pending'}
        </Tag>
      ),
    },
    {
      title: 'Time',
      dataIndex: 'processed_at',
      key: 'processed_at',
      render: (date) => formatDate(date),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          onClick={() => showTransactionDetail(record)}
        >
          Details
        </Button>
      ),
    },
  ];

  if (!user || !user.is_admin) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Text type="danger">You do not have permission to access this page</Text>
      </div>
    );
  }

  return (
    <div>
      <Title level={2}>
        <Space>
          <BarChartOutlined />
          Payment History Management
        </Space>
      </Title>

      {/* Statistics Cards */}
      {stats && (
        <Row gutter={16} style={{ marginBottom: '24px' }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Transactions"
                value={stats.total_transactions}
                prefix={<DollarOutlined />}
                loading={statsLoading}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Revenue"
                value={stats.total_amount}
                formatter={(value) => formatPrice(value)}
                prefix={<DollarOutlined />}
                loading={statsLoading}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Characters Added"
                value={stats.total_chars_added}
                formatter={(value) => value.toLocaleString()}
                loading={statsLoading}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Average per Transaction"
                value={stats.average_amount}
                formatter={(value) => formatPrice(value)}
                prefix={<DollarOutlined />}
                loading={statsLoading}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Filters */}
      <Card style={{ marginBottom: '16px' }}>
        <Row gutter={16} align="middle">
          <Col span={6}>
            <Input
              placeholder="User ID"
              value={filters.user_id}
              onChange={(e) => setFilters(prev => ({ ...prev, user_id: e.target.value || null }))}
              allowClear
            />
          </Col>
          <Col span={8}>
            <RangePicker
              style={{ width: '100%' }}
              onChange={(dates) => {
                setFilters(prev => ({
                  ...prev,
                  start_date: dates ? dates[0].format('YYYY-MM-DD') : null,
                  end_date: dates ? dates[1].format('YYYY-MM-DD') : null
                }));
              }}
            />
          </Col>
          <Col span={10}>
            <Space>
              <Button 
                type="primary" 
                icon={<SearchOutlined />}
                onClick={handleSearch}
                loading={loading}
              >
                Search
              </Button>
              <Button onClick={handleReset}>
                Reset
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadPaymentHistory}
                loading={loading}
              >
                Refresh
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Transactions Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={transactions}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} transactions`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Transaction Detail Modal */}
      <Modal
        title="Transaction Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={600}
      >
        {selectedTransaction && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label="Transaction ID">
              {selectedTransaction.transaction_id}
            </Descriptions.Item>
            <Descriptions.Item label="User">
              {selectedTransaction.user_name} ({selectedTransaction.user_email})
            </Descriptions.Item>
            <Descriptions.Item label="Amount">
              <Text strong style={{ color: '#52c41a' }}>
                {formatPrice(selectedTransaction.amount)}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Plan">
              {selectedTransaction.plan_name} (+{selectedTransaction.plan_chars.toLocaleString()} chars)
            </Descriptions.Item>
            <Descriptions.Item label="Transfer Reference">
              <Text code>{selectedTransaction.content}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={selectedTransaction.status === 'processed' ? 'green' : 'orange'}>
                {selectedTransaction.status === 'processed' ? 'Processed' : 'Pending'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Processing Time">
              {formatDate(selectedTransaction.processed_at)}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default AdminPaymentHistory;